<script lang="ts">
	import { format, locale } from 'svelte-i18n';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { profile_data, user_data } from 'src/stores/account/user';
	import { navigateToShop } from '../Lists/list-helpers';
	import { ButtonV2 } from '@Shelly/shelly-design-system';
	import { getPromoExpiryText } from 'src/helpers/promo_helpers';
	import { PROMOTIONS } from 'src/constants';

	import PromoModal from 'src/components/Promotions/organisms/PromoModal.svelte';
	import PromoModalBanner from 'src/components/Promotions/molecules/PromoModalBanner.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { createEventDispatcher } from 'svelte';

	export let showModal = false;

	const dispatch = createEventDispatcher();

	const onCloseModal = (fromShopButton = false) => {
		if (showModal) {
			showModal = false;
			if (!$profile_data?.promotions?.springDeals2026) {
				save_profile('promotions', {
					...$profile_data?.promotions,
					springDeals2026: new Date().getTime(),
				});
			}
			if (!fromShopButton) {
				const sd = { ...$profile_data?.aep?.sd };
				sd.closeOnPromotionModal = (sd?.closeOnPromotionModal || 0) + 1;
				save_profile('aep', { ...$profile_data.aep, sd });
			}
			dispatch('close');
		}
	};

	const handleSaveForLater = () => {
		onCloseModal();
	};

	const handleOpenShop = () => {
		onCloseModal(true);
		const sd = { ...$profile_data?.aep?.sd };
		sd.clickOnShopNowButton = (sd?.clickOnShopNowButton || 0) + 1;
		save_profile('aep', { ...$profile_data.aep, sd });
		if ($user_data?.timezone.startsWith('America/')) {
			navigateToShop(
				'https://us.shelly.com/pages/spring-deals?utm_source=shellyapp&utm_medium=referral&utm_campaign=spring_deals',
			);
		} else {
			navigateToShop(
				`https://www.shelly.com/${['de', 'fr', 'it'].includes($locale) ? `${$locale}/` : ''}pages/spring-deals?utm_source=shellyapp&utm_medium=referral&utm_campaign=spring_deals`,
			);
		}
	};

	$: expiryText = $format(
		getPromoExpiryText(PROMOTIONS.SPRING_DEALS_2026.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.SPRING_DEALS_2026.END_DATE)?.object,
	);

	$: image = $user_data?.timezone?.startsWith('America/')
		? './images/shelly_spring_deals_2026_App_us.png'
		: `./images/shelly_spring_deals_2026_App_${['de', 'fr', 'it'].includes($locale) ? $locale : 'en'}.png`;
</script>

<PromoModal
	{showModal}
	title={$format('title_spring_deals', { default: 'Spring Deals Are Here' })}
	closeAriaLabel={$format('button_close', { default: 'Close' })}
	on:close={handleSaveForLater}
	backdropClose
>
	<PromoModalBanner
		{image}
		alt={$format('title_spring_deals', { default: 'Spring Deals Are Here' })}
		activeBadgeLabel={$format('label_active', { default: 'Active' })}
		aspectRatio="1200 / 520"
		overlayPosition="top"
		{expiryText}
	/>

	<div class="content-section">
		<div class="content-text">
			<p class="content-paragraph text-bold">
				{$format('text_spring_deals_intro', {
					default: 'Smarter Living Starts Here',
				})}
			</p>
			<p class="content-paragraph text-300">
				{$format('text_spring_deals_description', {
					default:
						'Bring your home to the next level with smart solutions designed for comfort and savings - now at our best prices this season.',
				})}
			</p>
		</div>
	</div>

	<svelte:fragment slot="footer">
		<div class="width-full d-flex justify-content-end gap-s">
			<ButtonV2
				variant="secondary"
				on:click={handleSaveForLater}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			>
				{capFirstChar($format('button_maybe_later', { default: 'Maybe later' }))}
			</ButtonV2>
			<ButtonV2
				variant="primary"
				on:click={handleOpenShop}
				icon="fas fa-shop"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			>
				{$format('button_shop_now', { default: 'Shop Now' })}
			</ButtonV2>
		</div>
	</svelte:fragment>
</PromoModal>

<style>
	.content-section {
		display: flex;
		flex-direction: column;
		gap: 24px;
		padding: 24px 24px 0px 24px;
	}

	.content-text {
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 400;
		line-height: 19px;
		color: var(--color-white, #ffffff);
	}

	.content-paragraph {
		margin: 0 0 10px 0;
	}

	@media (max-width: 639px) {
		.content-section {
			padding: 16px 16px 0px 16px;
		}
	}
</style>
